/**
 * 修补工具（补齐 registry.ts 早先登记的占位工具）。
 *
 * Photoshop 的修补工具：先套索圈一块要修的区域，再把这块区域拖到"看着
 * 干净"的地方，松手后原位置的内容被替换成拖到的那块——本质是"选区内
 * 容搬家 + 无缝融合"。真正的无缝融合是梯度域泊松融合，这套算法储备里
 * 没有（同 buffer.ts 里修复画笔那条注释的取舍）；这里用均色偏移近似。
 *
 * 关键点：均色不能拿"选区本身现状"当目的地基准——选区本身就是待修的
 * 瑕疵，它的颜色就是问题所在，拿它当基准会让色差刚好把新内容纠正回
 * 瑕疵原来的颜色，等于白修。真正该匹配的是选区**周围一圈**（挖洞边界
 * 之外的邻近像素）：目的地基准 = 选区外圈现状的均色，源基准 = 同一圈
 * 平移 offset 之后的均色——这才是"新内容的色调该向谁看齐"的正确参照，
 * 边界处颜色越接近，缝合处越不显眼。
 */
import { compositeDocument } from './composite';
import { applyFill } from '../engine/fill/apply';
import type { SelectionMask } from '../engine/selection/mask';
import type { AppStore } from './store';

/**
 * 把 `mask` 圈定的区域替换成 (mask 平移 offsetX/offsetY 之后) 的内容，
 * 并做一次简单的色彩匹配。`offsetX`/`offsetY` 均为 0（没拖动）时是无操作。
 * 一次调用 = 一条历史。返回是否有修改发生。
 */
export function patchFill(
  store: AppStore,
  mask: SelectionMask,
  offsetX: number,
  offsetY: number,
): boolean {
  if (offsetX === 0 && offsetY === 0) return false;
  const document = store.document;
  if (document === null) return false;
  const target = store.activeRasterLayerId();
  if (target === null) return false;
  const bounds = mask.contentBounds();
  if (bounds === null) return false;

  const { width: docWidth, height: docHeight } = document.size;
  const composite = compositeDocument(document, store.tiles);
  const sampleAt = (x: number, y: number): readonly [number, number, number, number] | null => {
    if (x < 0 || y < 0 || x >= docWidth || y >= docHeight) return null;
    const at = (y * docWidth + x) * 4;
    return [composite.pixels[at]!, composite.pixels[at + 1]!, composite.pixels[at + 2]!, composite.pixels[at + 3]!];
  };

  // 色彩匹配的基准取选区外圈（挖洞边界之外几像素的邻近带），不能用选区
  // 本身现状——那正是待修的瑕疵，见文件头注释。
  const RING = 6;
  const rx0 = Math.max(0, bounds.x - RING);
  const ry0 = Math.max(0, bounds.y - RING);
  const rx1 = Math.min(docWidth, bounds.x + bounds.width + RING);
  const ry1 = Math.min(docHeight, bounds.y + bounds.height + RING);
  let srcSumR = 0, srcSumG = 0, srcSumB = 0;
  let dstSumR = 0, dstSumG = 0, dstSumB = 0;
  let weightSum = 0;
  const ringRow = new Uint8Array(rx1 - rx0);
  for (let y = ry0; y < ry1; y++) {
    mask.readRow(y, rx0, rx1, ringRow);
    for (let x = rx0; x < rx1; x++) {
      if (ringRow[x - rx0]! > 0) continue; // 选区内部不算"外圈"
      const dest = sampleAt(x, y);
      const src = sampleAt(x + offsetX, y + offsetY);
      if (dest === null || dest[3] === 0 || src === null || src[3] === 0) continue;
      dstSumR += dest[0]; dstSumG += dest[1]; dstSumB += dest[2];
      srcSumR += src[0]; srcSumG += src[1]; srcSumB += src[2];
      weightSum += 1;
    }
  }
  const correction =
    weightSum > 0
      ? { r: (dstSumR - srcSumR) / weightSum, g: (dstSumG - srcSumG) / weightSum, b: (dstSumB - srcSumB) / weightSum }
      : { r: 0, g: 0, b: 0 };
  const clamp = (v: number): number => Math.min(255, Math.max(0, Math.round(v)));

  const transaction = store.beginPixelTransaction('修补');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    (x, y) => {
      const src = sampleAt(x + offsetX, y + offsetY);
      if (src === null) return [0, 0, 0, 0];
      return [clamp(src[0] + correction.r), clamp(src[1] + correction.g), clamp(src[2] + correction.b), src[3]];
    },
    transaction,
    { selection: mask },
  );
  if (touched.length === 0) return false;
  store.commitPixels('修补', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
